import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Demo file content for OpenClaw knowledge files
const DEMO_FILE_CONTENT = {
    'SOUL.md': `# Bot Personality & Sales Process

## Personality
You are a friendly, helpful sales assistant. You speak in a warm, professional tone.

## Communication Style
- Use short, clear messages
- Be conversational but professional
- Always be helpful and patient

## Sales Process
1. Greet the customer warmly
2. Understand their needs
3. Recommend products that fit
4. Answer questions honestly
5. Handle objections professionally
6. Close with next steps`,

    'IDENTITY.md': `# Bot Identity

## Basic Info
- **Bot Name:** Claw9 Assistant
- **Shop Name:** Claw9 Phone Shop
- **Role:** Sales Assistant
- **Language:** Vietnamese / English

## Contact Info
- **Phone:** +84 xxx xxx xxx
- **Hours:** 8:00 AM - 10:00 PM

## About the Shop
We are a leading phone retailer specializing in premium smartphones and accessories.`,

    'AGENTS.md': `# Agent Instructions

## At Start of Each Conversation
Read these files to understand context:
- IDENTITY.md (who you are)
- SOUL.md (how to behave)
- SALES_KNOWLEDGE.md (what you're selling)

## Rules
1. Never make up product information
2. Always check SALES_KNOWLEDGE.md for prices
3. Be honest if you don't know something
4. Offer to connect with human if needed`,

    'SALES_KNOWLEDGE.md': `# Sales Knowledge Base

## Products

### iPhone 15 Pro Max
- **Price:** 29,990,000 VND
- **Colors:** Natural Titanium, Blue Titanium, White Titanium, Black Titanium
- **Storage:** 256GB, 512GB, 1TB

### Samsung Galaxy S24 Ultra
- **Price:** 26,990,000 VND
- **Colors:** Titanium Gray, Titanium Violet, Titanium Yellow, Titanium Black

## FAQ

**Q: Do you offer installment payment?**
A: Yes! We offer 0% installment for 6-12 months with major credit cards.

**Q: What's the warranty policy?**
A: All products come with official manufacturer warranty.`,

    'knowledge_base.csv': `product_name,price,category,stock
iPhone 15 Pro Max,29990000,smartphone,50
iPhone 15 Pro,25990000,smartphone,75
Samsung Galaxy S24 Ultra,26990000,smartphone,40
Samsung Galaxy S24+,22990000,smartphone,60
AirPods Pro 2,5990000,accessories,100
Samsung Galaxy Buds2 Pro,4990000,accessories,80`
};

// Default OpenClaw files
const DEFAULT_FILES = [
    'SOUL.md', 'IDENTITY.md', 'AGENTS.md',
    'SALES_KNOWLEDGE.md', 'knowledge_base.csv'
];

/**
 * Panel for reading and writing knowledge files in a workspace
 * Currently runs in demo mode with local data
 */
function FileEditorPanel({ workspaceId, className = '' }) {
    const [files, setFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileContent, setFileContent] = useState('');
    const [editedContent, setEditedContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Initialize with default files
    useEffect(() => {
        const fileList = DEFAULT_FILES.map(filename => ({
            name: filename
        }));
        setFiles(fileList);
    }, [workspaceId]);

    const handleFileSelect = (file) => {
        if (hasChanges) {
            const confirmDiscard = window.confirm('You have unsaved changes. Discard them?');
            if (!confirmDiscard) return;
        }

        // Get demo content for this file
        const content = DEMO_FILE_CONTENT[file.name] || `# ${file.name}\n\nContent for ${file.name}`;

        setSelectedFile(file);
        setFileContent(content);
        setEditedContent(content);
        setHasChanges(false);
    };

    const handleContentChange = (e) => {
        const newContent = e.target.value;
        setEditedContent(newContent);
        setHasChanges(newContent !== fileContent);
    };

    const handleSave = async () => {
        if (!selectedFile) return;

        setIsSaving(true);

        // Simulate save delay
        await new Promise(resolve => setTimeout(resolve, 500));

        setFileContent(editedContent);
        setHasChanges(false);
        setIsSaving(false);
    };

    const handleDelete = (file) => {
        const confirmDelete = window.confirm(`Delete ${file.name}? This action cannot be undone.`);
        if (!confirmDelete) return;

        setFiles(prev => prev.filter(f => f.name !== file.name));
        if (selectedFile?.name === file.name) {
            setSelectedFile(null);
            setFileContent('');
            setEditedContent('');
        }
    };

    const handleDiscard = () => {
        setEditedContent(fileContent);
        setHasChanges(false);
    };

    const getFileIcon = (file) => {
        const ext = file.name?.split('.').pop()?.toLowerCase();
        if (ext === 'md' || ext === 'txt') {
            return (
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            );
        }
        if (ext === 'csv') {
            return (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            );
        }
        return (
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
        );
    };

    return (
        <div className={`flex h-96 border border-gray-200 rounded-lg overflow-hidden ${className}`}>
            {/* File List */}
            <div className="w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto">
                <div className="p-3 border-b border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700">Knowledge Files</h4>
                </div>
                {files.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500 text-center">
                        No files found
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {files.map(file => (
                            <li
                                key={file.name}
                                className={`
                                    flex items-center justify-between px-3 py-2 cursor-pointer
                                    ${selectedFile?.name === file.name ? 'bg-indigo-50' : 'hover:bg-gray-100'}
                                `}
                            >
                                <button
                                    onClick={() => handleFileSelect(file)}
                                    className="flex items-center gap-2 flex-1 text-left"
                                >
                                    {getFileIcon(file)}
                                    <span className="text-sm text-gray-700 truncate">
                                        {file.name}
                                    </span>
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(file);
                                    }}
                                    className="text-gray-400 hover:text-red-500 p-1"
                                    title="Delete file"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Editor */}
            <div className="flex-1 flex flex-col">
                {selectedFile ? (
                    <>
                        {/* Editor Header */}
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">
                                    {selectedFile.name}
                                </span>
                                {hasChanges && (
                                    <span className="text-xs text-orange-600 font-medium">
                                        (unsaved)
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                {hasChanges && (
                                    <button
                                        onClick={handleDiscard}
                                        className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800"
                                    >
                                        Discard
                                    </button>
                                )}
                                <button
                                    onClick={handleSave}
                                    disabled={!hasChanges || isSaving}
                                    className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>

                        {/* Editor Content */}
                        <div className="flex-1 overflow-hidden bg-white">
                            <textarea
                                value={editedContent}
                                onChange={handleContentChange}
                                className="w-full h-full p-4 font-mono text-sm resize-none focus:outline-none bg-white text-gray-900"
                                placeholder="File content..."
                            />
                        </div>
                    </>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                        Select a file to view and edit
                    </div>
                )}
            </div>
        </div>
    );
}

FileEditorPanel.propTypes = {
    workspaceId: PropTypes.string.isRequired,
    className: PropTypes.string
};

export default FileEditorPanel;
